import React, { useState } from 'react';
import Dialog, { Content, DialogActions, Title } from 'ui/views/dialogs/Dialog';
import Button from 'ui/elements/buttons/Button';
import { KpiWithDisplay, DisplayType, KpiEmbed, ComparisonPeriod } from '../types';
import KPIGraph from '../KPIGraph';
import { MenuItem, Select, Slider } from '@mui/material';
import Label from 'ui/elements/form/Label';
import { FormControlLabel, Switch as MuiSwitch } from '@mui/material';
import { TinyCompanyDTO } from 'types/company';
import { getValuesToEmbed } from '../utils';

interface Props {
  kpi: KpiWithDisplay;
  company: TinyCompanyDTO;
  onClose: () => void;
  onEmbedCreated: (snapshot: KpiEmbed) => void;
  existingEmbed?: KpiEmbed;
}

export default function ShareKpiDialog({
  kpi,
  company,
  onClose,
  onEmbedCreated: onEmbedCreated,
  existingEmbed: existingEmbed,
}: Props) {
  const kpiDetails = kpi.details;
  const firstDisplay = kpi.display;

  const [displayType, setDisplayType] = useState<DisplayType>(
    existingEmbed?.displayConfig.displayType || firstDisplay?.displayType || 'LineChart',
  );

  // Initialize dataPointCount from existingEmbed, or from display's maxDataPoints for bar charts, or default to 12
  const [dataPointCount, setDataPointCount] = useState(() => {
    if (existingEmbed?.displayConfig.dataPointCount) {
      return existingEmbed.displayConfig.dataPointCount;
    }
    const currentDisplayType = existingEmbed?.displayConfig.displayType || firstDisplay?.displayType || 'LineChart';
    if (currentDisplayType === 'BarChart' && firstDisplay?.maxDataPoints) {
      return firstDisplay.maxDataPoints;
    }
    return 12;
  });
  const showXAxis = existingEmbed?.displayConfig.showXAxis ?? firstDisplay?.showXAxis ?? true;
  const showYAxis = existingEmbed?.displayConfig.showYAxis ?? firstDisplay?.showYAxis ?? true;
  const trendIndicator = existingEmbed?.displayConfig.trendIndicator || firstDisplay?.trendIndicator || 'Up';

  const [comparisonEnabled, setComparisonEnabled] = useState(
    existingEmbed?.displayConfig.comparisonEnabled ?? firstDisplay?.comparisonEnabled ?? false,
  );
  const [comparisonPeriod, setComparisonPeriod] = useState<ComparisonPeriod>(
    existingEmbed?.displayConfig.comparisonPeriod || firstDisplay?.comparisonPeriod || 'Month',
  );

  // Helper to compute required data point count for NumberIndicator
  const getDataPointCountForNumberIndicator = (enabled: boolean, period: ComparisonPeriod) => {
    if (!enabled) return 1;
    return period === 'Year' ? 13 : 2;
  };

  const handleDisplayTypeChange = (newType: DisplayType) => {
    setDisplayType(newType);

    if (newType === 'NumberIndicator') {
      // For NumberIndicator, set data points based on comparison settings
      setDataPointCount(getDataPointCountForNumberIndicator(comparisonEnabled, comparisonPeriod));
    } else if (newType === 'BarChart') {
      // Bar chart always compares with previous year
      setComparisonPeriod('Year');
      // For charts, ensure at least 3 points
      if (dataPointCount < 3) {
        setDataPointCount(12);
      }
    } else if (dataPointCount < 3) {
      // For other charts, ensure at least 3 points
      setDataPointCount(12);
    }
  };

  const handleComparisonEnabledChange = (enabled: boolean) => {
    setComparisonEnabled(enabled);

    // Update data point count for NumberIndicator
    if (displayType === 'NumberIndicator') {
      setDataPointCount(getDataPointCountForNumberIndicator(enabled, comparisonPeriod));
    }
  };

  const handleComparisonPeriodChange = (period: ComparisonPeriod) => {
    setComparisonPeriod(period);

    // Update data point count for NumberIndicator with comparison enabled
    if (displayType === 'NumberIndicator' && comparisonEnabled) {
      setDataPointCount(getDataPointCountForNumberIndicator(true, period));
    }
  };

  // Get values to embed - includes comparison data when needed
  const valuesToEmbed = getValuesToEmbed(kpi.values, displayType, comparisonEnabled, comparisonPeriod, dataPointCount);

  // Preview uses the same values as embed - dataPointCount controls how many bars KPIGraph shows
  const previewValues = valuesToEmbed;

  const handleCreate = () => {
    const now = new Date().toISOString();

    // Date range is always based on the last dataPointCount (the main bars shown)
    const mainValues = kpi.values.slice(-dataPointCount);

    const kpiEmbed: KpiEmbed = {
      id: existingEmbed?.id || `snapshot-${Date.now()}`, // Preserve ID when editing
      kpiId: kpiDetails.id,
      displayConfig: {
        displayType,
        dataPointCount,
        dateRange: {
          start: mainValues[0]?.timestamp || now,
          end: mainValues[mainValues.length - 1]?.timestamp || now,
        },
        showXAxis,
        showYAxis,
        comparisonEnabled,
        comparisonPeriod,
        trendIndicator,
      },
      metadata: {
        kpiName: kpiDetails.name,
        unitOfMeasure: kpiDetails.unitOfMeasure || '',
        companyId: company.id,
        companySlug: company.slug,
      },
      values: valuesToEmbed, // Embed the values (includes comparison data if needed)
    };

    onEmbedCreated(kpiEmbed);
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" mobileLayout="fullscreen">
      <Title onClose={onClose}>{existingEmbed ? 'Edit KPI Embed' : 'Share KPI'}</Title>
      <Content>
        <div className="u-content-spacing-bottom">
          <h5 className="text-h5 u-content-spacing-bottom">Preview</h5>
          <KPIGraph
            name={kpiDetails.name}
            unitOfMeasure={kpiDetails.unitOfMeasure}
            values={previewValues}
            displayType={displayType}
            showXAxis={showXAxis}
            showYAxis={showYAxis}
            comparisonEnabled={comparisonEnabled}
            comparisonPeriod={comparisonPeriod}
            trendIndicator={trendIndicator}
            dataPointCount={dataPointCount}
          />
        </div>

        <div className="u-content-spacing-bottom">
          <Label>Display Type</Label>
          <Select
            value={displayType}
            onChange={e => handleDisplayTypeChange(e.target.value as DisplayType)}
            fullWidth
            size="small"
          >
            <MenuItem value="LineChart">Line Chart</MenuItem>
            <MenuItem value="BarChart">Bar Chart</MenuItem>
            <MenuItem value="NumberIndicator">Number</MenuItem>
          </Select>
        </div>

        {/* Only show data points slider for charts, not NumberIndicator */}
        {displayType !== 'NumberIndicator' && (
          <div className="u-content-spacing-bottom">
            <Label>Data Points (months)</Label>
            <Slider
              value={dataPointCount}
              onChange={(_e: Event, value: number | number[]) => setDataPointCount(value as number)}
              min={3}
              max={24}
              step={3}
              marks={[
                { value: 3, label: '3' },
                { value: 6, label: '6' },
                { value: 9, label: '9' },
                { value: 12, label: '12' },
                { value: 15, label: '15' },
                { value: 18, label: '18' },
                { value: 21, label: '21' },
                { value: 24, label: '24' },
              ]}
              valueLabelDisplay="auto"
            />
          </div>
        )}

        {/* Comparison controls */}
        {displayType === 'BarChart' && (
          <div className="u-content-spacing-bottom">
            <FormControlLabel
              control={
                <MuiSwitch
                  checked={comparisonEnabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleComparisonEnabledChange(e.target.checked)}
                />
              }
              label="Show Year-over-Year Comparison"
            />
            {comparisonEnabled && (
              <p className="text-metadata u-color-grey-dark u-quarter-spacing-top">
                Previous year values shown as grey bars
              </p>
            )}
          </div>
        )}

        {displayType === 'NumberIndicator' && (
          <div className="u-content-spacing-bottom">
            <FormControlLabel
              control={
                <MuiSwitch
                  checked={comparisonEnabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleComparisonEnabledChange(e.target.checked)}
                />
              }
              label="Enable Comparison"
            />
          </div>
        )}

        {comparisonEnabled && displayType === 'NumberIndicator' && (
          <>
            <div className="u-content-spacing-bottom">
              <Label>Comparison Period</Label>
              <Select
                value={comparisonPeriod}
                onChange={e => handleComparisonPeriodChange(e.target.value as ComparisonPeriod)}
                fullWidth
                size="small"
              >
                <MenuItem value="Month">Previous month</MenuItem>
                <MenuItem value="Year">Previous year</MenuItem>
              </Select>
            </div>
          </>
        )}
      </Content>
      <DialogActions>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button kind="primary" onClick={handleCreate}>
          {existingEmbed ? 'Update' : 'Insert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
